import { CpsAlert } from "corpus";
import { type FragmentType, graphql, useFragment } from "@repo/graphql-types";
import { Edit1 } from "@repo/icons";
import {
  PageTitleDescription,
  PageTitleMainContent,
  PageTitleRoot,
} from "@/components/page-title/page-title-root.tsx";
import {
  Item,
  ItemContent,
  ItemContentEnd,
  ItemMainContent,
  ListRoot,
} from "@/components/list";

export const PersonalizeAppointmentFormFragment = graphql(/* GraphQL */ `
  fragment PersonalizeAppointmentFormFragment on query_root {
    personalizeAppointment: usuariosProfissionaisOrientacoesSecretarias {
      professionalInfos: informacoesSobreProfissional
      appointmentInfos: informacoesSobreAgendamento
      restrictions: informacoesSobreRestricoes
      faq: informacoesDuvidasFrequentes
    }
  }
`);

export const PersonalizeAppointmentForm = ({
  data,
}: {
  data: FragmentType<typeof PersonalizeAppointmentFormFragment> | null | undefined;
}): JSX.Element => {
  const fragmentData = useFragment(PersonalizeAppointmentFormFragment, data);
  const professionalData = fragmentData?.personalizeAppointment[0];

  return (
    <>
      <PageTitleRoot>
        <PageTitleMainContent>Personalizar atendimento</PageTitleMainContent>
        <PageTitleDescription>
          Adicione informações complementares para que as secretárias possam direcionar
          seus pacientes de forma mais assertiva, garantindo agendamentos alinhados às
          suas orientações. Lembre-se de que sua agenda e configurações de atendimento já
          estão definidas.
        </PageTitleDescription>
      </PageTitleRoot>
      <div className="my-4">
        <CpsAlert
          type="info"
          title="Essas informações serão vistas apenas pelo nosso time de secretárias."
        />
      </div>
      <ListRoot>
        <Item
          className="flex gap-1"
          linkProps={{
            to: "/settings/assist/secretary-details/professional-infos",
          }}
          noChevron
        >
          <ItemContent>
            <ItemMainContent className="text-md text-neutral-700">
              Qual seu diferencial como Profissional?
            </ItemMainContent>
            <p className="text-sm text-gray-600 font-normal mt-2 line-clamp-3 whitespace-pre-line">
              {professionalData?.professionalInfos
                ? professionalData.professionalInfos
                : "Quais são os principais motivos que levam outras pessoas a te indicarem?\n Escreva até 3 tópicos que resumem seu diferencial."}
            </p>
          </ItemContent>
          <ItemContentEnd className="self-start">
            <Edit1 size={24} />
          </ItemContentEnd>
        </Item>
        <Item
          className="flex gap-1"
          linkProps={{
            to: "/settings/assist/secretary-details/appointment-infos",
          }}
          noChevron
        >
          <ItemContent>
            <ItemMainContent className="text-md text-neutral-700">
              Orientações para Agendamento
            </ItemMainContent>
            <p className="text-sm text-gray-600 font-normal mt-2 line-clamp-3 whitespace-pre-line">
              {professionalData?.appointmentInfos
                ? professionalData.appointmentInfos
                : "Quais informações são essenciais no momento do agendamento? Informe diretrizes claras para facilitar o processo."}
            </p>
          </ItemContent>
          <ItemContentEnd className="self-start">
            <Edit1 size={24} />
          </ItemContentEnd>
        </Item>
        <Item
          className="flex gap-1"
          linkProps={{ to: "/settings/assist/secretary-details/restrictions" }}
          noChevron
        >
          <ItemContent>
            <ItemMainContent className="text-md text-neutral-700">
              Restrições de Agendamento
            </ItemMainContent>
            <p className="text-sm text-gray-600 font-normal mt-2 line-clamp-3 whitespace-pre-line">
              {professionalData?.restrictions
                ? professionalData.restrictions
                : "Indique situações ou práticas que devem ser evitadas."}
            </p>
          </ItemContent>
          <ItemContentEnd className="self-start">
            <Edit1 size={24} />
          </ItemContentEnd>
        </Item>
        <Item
          className="flex gap-1"
          linkProps={{ to: "/settings/assist/secretary-details/faq" }}
          noChevron
        >
          <ItemContent>
            <ItemMainContent className="text-md text-neutral-700">
              Dúvidas frequentes registradas pelo time de atendimento ao paciente
            </ItemMainContent>
            <p className="text-sm text-gray-600 font-normal mt-2 line-clamp-3 whitespace-pre-line">
              {professionalData?.faq
                ? professionalData.faq
                : "Aqui estão dúvidas comuns de pacientes identificadas pelo nosso time de atendimento, além de outras informações importantes sobre seu consultório."}
            </p>
          </ItemContent>
          <ItemContentEnd className="self-start">
            <Edit1 size={24} />
          </ItemContentEnd>
        </Item>
      </ListRoot>
    </>
  );
};
